import { COLLAPSED_BIN_HEIGHT } from '@/components/canvas/shared/canvasPanelConstants'
import type { BinPreviewItem } from '../types'
import type { ShotColorLabel } from '@/types'

export const CELL_SIZE = 48
export const CELL_GAP = 4
export const SHEET_PADDING = 6

/** Room for the move, collapse, and more glyphs: 3 × (6px padding + 16px glyph) + 6px margin. */
export const COLLAPSED_MIN_WIDTH = 72

/**
 * A nested bin inside a closed bin is not a cell — it is an outlined box:
 * the bin's name on the first line, then its children as cells aligned to
 * the name, recursively. The outline (and a faint fill) take the nested
 * bin's colour label, so a closed bin still shows which bins are inside it
 * and how they are labelled.
 */
export const SHEET_GROUP_PADDING = 6
export const SHEET_GROUP_TITLE_HEIGHT = 14
export const SHEET_GROUP_TITLE_FONT_SIZE = 9
export const SHEET_GROUP_TITLE_GAP = 4
/** Air between a group box and whatever precedes it. */
export const SHEET_GROUP_GAP = 6

export interface SheetCell {
  item: BinPreviewItem
  x: number
  y: number
}

export interface SheetGroup {
  title: string
  x: number
  y: number
  width: number
  height: number
  colorLabel?: ShotColorLabel
}

export interface SheetLayout {
  /** Columns available to the top-level cells. */
  columns: number
  /** Rows of top-level cells (nested-bin groups not included). */
  rows: number
  /** Closed-bin body height. */
  height: number
  cellSize: number
  gap: number
  padding: number
  /** Every cell in render order, positioned relative to the bin body. */
  cells: SheetCell[]
  /** Every nested-bin box in render order (outer boxes before the boxes inside them). */
  groups: SheetGroup[]
}

export function computeBinCollapsedWidth(storedWidth: number): number {
  return Math.max(COLLAPSED_MIN_WIDTH, storedWidth)
}

function columnsFor(availableWidth: number): number {
  return Math.max(1, Math.floor((availableWidth + CELL_GAP) / (CELL_SIZE + CELL_GAP)))
}

/**
 * Lay out a closed bin's contact sheet. `items` may be a plain count (every
 * entry is a cell) or the real item tree. Cells wrap left-to-right inside
 * the available width; nested bins follow as outlined boxes.
 */
export function computeBinContactSheetLayout(width: number, items: readonly BinPreviewItem[] | number): SheetLayout {
  const list: readonly BinPreviewItem[] = typeof items === 'number' ? Array.from({ length: items }, () => ({})) : items
  const inner = Math.max(0, width - 2 * SHEET_PADDING)
  const columns = columnsFor(inner)
  const cells: SheetCell[] = []
  const groups: SheetGroup[] = []
  const rootCells = list.filter(item => item.kind !== 'bin').length
  const rows = Math.ceil(rootCells / columns)
  const base = { columns, rows, cellSize: CELL_SIZE, gap: CELL_GAP, padding: SHEET_PADDING, cells, groups }
  if (list.length === 0) return { ...base, height: COLLAPSED_BIN_HEIGHT }
  const yEnd = layoutGroup(list, SHEET_PADDING, inner, SHEET_PADDING, cells, groups)
  return { ...base, height: yEnd + SHEET_PADDING }
}

function layoutGroup(
  items: readonly BinPreviewItem[],
  x0: number,
  availableWidth: number,
  yStart: number,
  cells: SheetCell[],
  groups: SheetGroup[],
): number {
  let y = yStart
  const columns = columnsFor(availableWidth)
  let cellIndex = 0
  for (const item of items) {
    if (item.kind === 'bin') continue
    cells.push({
      item,
      x: x0 + (cellIndex % columns) * (CELL_SIZE + CELL_GAP),
      y: y + Math.floor(cellIndex / columns) * (CELL_SIZE + CELL_GAP),
    })
    cellIndex++
  }
  const cellRows = Math.ceil(cellIndex / columns)
  if (cellRows > 0) y += cellRows * CELL_SIZE + (cellRows - 1) * CELL_GAP

  for (const item of items) {
    if (item.kind !== 'bin') continue
    if (cells.length > 0 || groups.length > 0) y += SHEET_GROUP_GAP
    const children = item.children ?? []
    const group: SheetGroup = {
      title: item.name ?? 'Bin',
      x: x0,
      y,
      width: availableWidth,
      height: 0,
      colorLabel: item.colorLabel,
    }
    groups.push(group)
    const innerX = x0 + SHEET_GROUP_PADDING
    const innerWidth = Math.max(CELL_SIZE, availableWidth - 2 * SHEET_GROUP_PADDING)
    let innerY = y + SHEET_GROUP_PADDING + SHEET_GROUP_TITLE_HEIGHT
    if (children.length > 0) {
      innerY = layoutGroup(children, innerX, innerWidth, innerY + SHEET_GROUP_TITLE_GAP, cells, groups)
    }
    group.height = innerY + SHEET_GROUP_PADDING - y
    y += group.height
  }
  return y
}

/** Every thumbnail URL in the tree, nested bins included — the atlas pin set. */
export function collectBinPreviewUrls(items: readonly BinPreviewItem[], out: string[] = []): string[] {
  for (const item of items) {
    if (item.url) out.push(item.url)
    if (item.children) collectBinPreviewUrls(item.children, out)
  }
  return out
}

/** Structural equality over the item tree — the fields the sheet actually renders. */
export function binPreviewItemsEqual(a: readonly BinPreviewItem[], b: readonly BinPreviewItem[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (x.url !== y.url || x.kind !== y.kind || x.colorLabel !== y.colorLabel || x.name !== y.name) return false
    if (!binPreviewItemsEqual(x.children ?? [], y.children ?? [])) return false
  }
  return true
}
